import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { MultiFactorError } from 'firebase/auth';
import { serverTimestamp } from 'firebase/firestore';
import type { Timestamp } from 'firebase/firestore';
import type { User } from '@shared/types';
import { isAdminRole } from '@shared/utils';
import type { AuthState, PermissionAction } from '../types';
import { RBAC_POLICIES } from '../types';
import {
  signInWithEmail,
  signOutUser,
  subscribeToAuthState,
} from '../services/auth.service';
import { fetchUser, createUserIfNotExists, isBootstrapAdmin } from '../services/user.service';

type AuthContextValue = AuthState & {
  signInWithEmail: (email: string, password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
  hasPermission: (resource: string, action: PermissionAction) => boolean;
  mfaPending: MultiFactorError | null;
  clearMfaPending: () => void;
};

function checkPermission(user: User | null, resource: string, action: PermissionAction): boolean {
  if (!user) return false;
  const policies = RBAC_POLICIES[user.role];
  return policies.some(
    (p) =>
      (p.resource === '*' || p.resource === resource) &&
      (p.actions as readonly PermissionAction[]).includes(action)
  );
}

function applyBootstrapAdminOverride(user: User): User {
  if (isAdminRole(user.role)) return user;
  return isBootstrapAdmin(user.email) ? { ...user, role: 'system_admin' } : user;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });
  const [mfaPending, setMfaPending] = useState<MultiFactorError | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToAuthState(async (firebaseUser) => {
      if (!firebaseUser) {
        setState({ user: null, isLoading: false, isAuthenticated: false });
        return;
      }
      await firebaseUser.getIdToken(true).catch(() => { /* リフレッシュ失敗は継続 */ });
      try {
        const email = firebaseUser.email ?? '';
        const displayName =
          firebaseUser.displayName ||
          email.split('@')[0] ||
          'ユーザー';
        const user = await createUserIfNotExists(firebaseUser.uid, email, displayName);
        setState({ user: applyBootstrapAdminOverride(user), isLoading: false, isAuthenticated: true });
      } catch {
        const fetched = await fetchUser(firebaseUser.uid);
        if (fetched) {
          setState({
            user: applyBootstrapAdminOverride(fetched),
            isLoading: false,
            isAuthenticated: true,
          });
          return;
        }
        const email = firebaseUser.email ?? '';
        const fallbackUser: User = {
          id: firebaseUser.uid,
          email,
          displayName: firebaseUser.displayName || email.split('@')[0] || 'ユーザー',
          role: isBootstrapAdmin(email) ? 'system_admin' : 'general',
          departmentId: null,
          organizationId: null,
          photoURL: firebaseUser.photoURL ?? null,
          mfaEnabled: false,
          lastLoginAt: null,
          isActive: true,
          createdAt: serverTimestamp() as unknown as Timestamp,
          updatedAt: serverTimestamp() as unknown as Timestamp,
          createdBy: firebaseUser.uid,
          updatedBy: firebaseUser.uid,
          isDeleted: false,
        };
        setState({ user: fallbackUser, isLoading: false, isAuthenticated: true });
      }
    });
    return unsubscribe;
  }, []);

  const handleSignInWithEmail = useCallback(async (email: string, password: string) => {
    const result = await signInWithEmail(email, password);
    return result.success ? null : (result.error ?? 'ログインに失敗しました。');
  }, []);

  const clearMfaPending = useCallback(() => { setMfaPending(null); }, []);

  const handleSignOut = useCallback(async () => { await signOutUser(); }, []);

  const hasPermission = useCallback(
    (resource: string, action: PermissionAction) => checkPermission(state.user, resource, action),
    [state.user]
  );

  const value: AuthContextValue = {
    ...state,
    signInWithEmail: handleSignInWithEmail,
    signOut: handleSignOut,
    hasPermission,
    mfaPending,
    clearMfaPending,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
